import { React, useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { useSelector } from "react-redux";
import AxiosApi from "../../Helper/axios.api";
import helper from "../../Helper/Helper";
import VoiceSelector from "./VoiceSelector";
import { set } from "date-fns";

const AnimationPreviews = forwardRef(({ 
  templateId, 
  thumb, 
  createPreviews, 
  isUpdate, 
  setIsUpdate,
  onVoiceSettingsChange
}, ref) => {
  const auth = useSelector((state) => state.auth);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  // Voice morphing is now treated as read-only (can't be toggled by user). We only track the initial voice model.
  const [initialVoiceSettings, setInitialVoiceSettings] = useState({
    voice_model: null
  });
  const [templateData, setTemplateData] = useState(null);
  const [state, setState] = useState({
    preview_state: "",
    previews: {},
    user_id: auth.userDeatils.user_id,
  });
  // Pagination for expressions (show 3 at a time)
  const [expressionPage, setExpressionPage] = useState(0);
  // Show 3 expression buttons per page; navigation arrows appear under label
  const EXPRESSIONS_PER_PAGE = 3;

  const { preview_state, previews, user_id } = state;
  const videoRef = useRef(null);

  useEffect(() => {
    getPreviews(templateId);
    getTemplateData(templateId);

    let interval;
    if (preview_state !== "Done") {
      interval = setInterval(() => {
        getPreviews(templateId)
      }, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };                                                                                                                                                                                                                
  }, [templateId, isUpdate, preview_state]);

  useEffect(() => {
    if (videoRef.current) {
      setVideoLoading(true);
      videoRef.current.load();
      
      const handleLoadedData = () => {
        setVideoLoading(false);
      };
      
      const handleError = () => {
        setVideoLoading(false);
      };
      
      videoRef.current.addEventListener('loadeddata', handleLoadedData);
      videoRef.current.addEventListener('error', handleError);
      
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadeddata', handleLoadedData);
          videoRef.current.removeEventListener('error', handleError);
        }
      };
    }
  }, [selectedPreview]);

  // Effect to initialize voice selection when both templateData and voices are available
  useEffect(() => {
    if (templateData && previews.voices && templateData.voice_model && !selectedVoice) {
      initializeVoiceSelection(previews.voices, templateData.voice_model);
    }
  }, [templateData, previews.voices, selectedVoice]);

  const getTemplateData = async (template_id) => {
    if (template_id !== '') {
      try {
        const res = await AxiosApi.withToken(
          auth.accessToken,
          `templates/${template_id}/details`, "get"
        );
        if (res.status && res.data) {
          setTemplateData(res.data);
          const voiceModel = res.data.voice_model || null;
          if (voiceModel == null)
          setInitialVoiceSettings({
            voice_model: voiceModel
          });
          
          // Note: Voice selection will be handled after voices are loaded in getPreviews
        }
      } catch (error) {
        console.error("Error fetching template data:", error);
      }
    }
  };

  const getPreviews = async (template_id) => {
    if (template_id !== '') {
      const res = await AxiosApi.withToken(
        auth.accessToken,
        'templates/previews', "post",
        { template_id: template_id, user_id: user_id }
      )
      if (res.status) {
        setState((state) => ({
          ...state,
          preview_state: res.data.state,
          previews: {
            speech: res.data.speech,
            poses: res.data.poses,
            expressions: res.data.expressions,
            voices: res.data.voices
          }
        }));
        
        // Initialize voice selection after voices are loaded
        if (res.data.voices && templateData) {
          initializeVoiceSelection(res.data.voices, templateData.voice_model);
        }
        
        if (res.data.state === "Done" && res.data.speech)
          setSelectedPreview((selectedPreview) => selectedPreview || res.data.speech);
      }
    }
  };

  const initializeVoiceSelection = (voices, savedVoiceModel) => {
    if (!voices || !savedVoiceModel) return;
    
    // Find the saved voice in the available voices
    let foundVoice = null;
    
    // Search through all gender categories  
    Object.keys(voices).forEach(gender => {
      if (voices[gender] && typeof voices[gender] === 'object') {
        // Each gender contains age categories
        Object.keys(voices[gender]).forEach(ageCategory => {
          if (Array.isArray(voices[gender][ageCategory])) {
            // Each age category contains an array of voice objects
            voices[gender][ageCategory].forEach(voice => {
              if (voice.name === savedVoiceModel) {
                foundVoice = voice;
              }
            });
          }
        });
      }
    });
    
    if (foundVoice) {
      setSelectedVoice(foundVoice);
      setInitialVoiceSettings({
        voice_model: foundVoice.name
      });
    } else {
      // This should not be possible as there is a default voice
      console.warn("Saved voice model not found:", savedVoiceModel);
    }
  };

  // Keep expression page in sync with selected expression
  useEffect(() => {
    if (selectedPreview && state.previews?.expressions?.length) {
      // Sort expressions alphabetically to match the display order
      const sortedExpressions = [...state.previews.expressions].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      const idx = sortedExpressions.findIndex(p => p.label === selectedPreview.label);
      if (idx !== -1) {
        const neededPage = Math.floor(idx / EXPRESSIONS_PER_PAGE);
        if (neededPage !== expressionPage) setExpressionPage(neededPage);
      }
    }
  }, [selectedPreview, state.previews?.expressions]);

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
    
    // Notify parent component about the change
    const voiceMorphingEnabled = !!templateData?.enable_morphing; // derive read-only flag
    if (onVoiceSettingsChange && voiceMorphingEnabled) {
      onVoiceSettingsChange({
        enable_morphing: voiceMorphingEnabled,
        voice_model: voice.name
      });
    }
  };

  // Function to get current voice settings for parent component
  const getCurrentVoiceSettings = () => {
    const voiceMorphingEnabled = !!templateData?.enable_morphing;
    return {
      enable_morphing: voiceMorphingEnabled,
      // If no selectedVoice yet (e.g., previews not loaded), treat current voice as initial backend voice
      voice_model: voiceMorphingEnabled
        ? (selectedVoice?.name || initialVoiceSettings.voice_model || null)
        : null
    };
  };

  const hasVoiceSettingsChanged = () => {
    const current = getCurrentVoiceSettings();
    // Only compare voice_model now (enable morphing is immutable from the user's perspective)
    return current.voice_model !== initialVoiceSettings.voice_model;
  };

  // Separate function to check if only the voice selection has changed
  const hasVoiceSelectionChanged = () => {
    const current = getCurrentVoiceSettings();

    // Only consider changed if a new concrete selectedVoice exists AND differs from initial.
    // If we haven't loaded voices yet (selectedVoice null), treat as unchanged to avoid false positive.
    if (!current.enable_morphing) return false;
    if (!selectedVoice?.name) return false;
    return selectedVoice.name !== initialVoiceSettings.voice_model;
  };

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    getCurrentVoiceSettings,
    hasVoiceSettingsChanged,
    hasVoiceSelectionChanged,
    resetInitialVoiceSettings: () => {
      const current = getCurrentVoiceSettings();
      setInitialVoiceSettings({
        voice_model: current.voice_model
      });
    }
  }));

  return (
    <>
      {/* Main content area */}
      <div className="uploading-block d-flex flex-wrap flex-sm-nowrap main-container">
        {/* Left column - Video preview */}
        <div className="col-md-5">
          <div className="video-preview-container p-3">
            <div className="video-frame">
              {/* Loading skeleton */}
              {videoLoading && (
                <div className="video-loading-skeleton">
                  <div className="loading-text">
                    Loading video...
                  </div>
                </div>
              )}
              
              {selectedPreview ? (
                <video 
                  controls 
                  autoPlay 
                  loop={selectedPreview.name !== "Speech"} 
                  className="video-player" 
                  ref={videoRef}
                  style={{ opacity: videoLoading ? 0 : 1 }}
                >
                  <source src={`${helper.ApiBaseUrl}${selectedPreview.url}`} />
                </video>
              ) : (
                <img
                  className="video-thumbnail"
                  src={`${helper.ApiBaseUrl}${thumb}`}
                  alt="Animation preview"
                />
              )}
              
              {/* Video overlay info */}
              <div className="video-overlay">
                {selectedPreview ? selectedPreview.name || 'Animation Preview' : 'Static Preview'}
              </div>
            </div>
          </div>

          {/* Selected Voice Audio Player - Only show when voice morphing is enabled */}
          {templateData?.enable_morphing && selectedVoice && selectedVoice.url && (
            <div className="selected-voice-player mt-4 p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="voice-info">
                  <div className="d-flex align-items-center mb-1">
                    <i className="fa fa-microphone me-2 voice-icon"></i>
                    <strong className="voice-name">
                      {selectedVoice.name}
                    </strong>
                  </div>
                  <small className="text-muted voice-description">
                    Preview of voice used during the therapy session
                  </small>
                </div>
              </div>
              
              <div className="audio-player-wrapper">
                <audio 
                  controls 
                  className="w-100 audio-player" 
                  key={selectedVoice.url}
                >
                  <source src={`${helper.ApiBaseUrl}${selectedVoice.url}`} type="audio/wav" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Controls */}
        <div className="col-md-7 controls-column">
          <div className="profile-form-block">
            <div className="pt-2 controls-content">
              {preview_state === "Running" ? (
                <div>
                  <div
                    type="button"
                    className="text-center upcoming-session-action-button session-item-active opacity-1"
                  >
                    Animation previews are being processed. Please wait...
                    <div className="spinner-border spinner-border-sm text-info ms-2" role="status"></div>
                  </div>
                  
                  {/* Show default voice message when voice is not self-chosen */}
                  {templateData?.enable_morphing && !templateData?.voice_self_chosen && (
                    <div className="alert alert-info mt-3 d-flex align-items-start" style={{
                      background: 'linear-gradient(135deg, #FFE4B5 0%, #FFD8A8 100%)',
                      border: '2px solid rgba(255, 140, 0, 0.5)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(255, 140, 0, 0.2)'
                    }}>
                      <i className="fa fa-info-circle me-2 mt-1" style={{color: '#FF8C00', fontSize: '18px'}}></i>
                      <div>
                        <strong style={{color: '#FF8C00'}}>Default AI Voice Selected</strong>
                        <p className="mb-0 mt-1" style={{fontSize: '13px', color: '#856404'}}>
                          Voice selection will be available once the animation previews are ready.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : preview_state === "Failed" || preview_state === "None" ? (
                <div className="text-center">
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    Animation preview generation failed.
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary upcoming-session-action-button"
                    title={"Retry Preview Generation"}
                    onClick={() => {
                      if (createPreviews) {
                        createPreviews(templateId).then(() => {
                          getPreviews(templateId);
                        });
                      }
                    }}
                    disabled={!createPreviews}
                  >
                    <i className="fa fa-refresh me-2"></i>
                    Retry Preview Generation
                  </button>
                  
                  {/* Show default voice message when voice is not self-chosen */}
                  {templateData?.enable_morphing && !templateData?.voice_self_chosen && (
                    <div className="alert alert-info mt-3 d-flex align-items-start" style={{
                      background: 'linear-gradient(135deg, #FFE4B5 0%, #FFD8A8 100%)',
                      border: '2px solid rgba(255, 140, 0, 0.5)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(255, 140, 0, 0.2)'
                    }}>
                      <i className="fa fa-info-circle me-2 mt-1" style={{color: '#FF8C00', fontSize: '18px'}}></i>
                      <div>
                        <strong style={{color: '#FF8C00'}}>Default AI Voice Selected</strong>
                        <p className="mb-0 mt-1" style={{fontSize: '13px', color: '#856404'}}>
                          Voice selection will be available after successfully generating the animation previews. Please retry above.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : preview_state === "Done" && (
                
                <div className="animation-controls-container">
                  {/* Video Animations Section */}
                  <div className="video-animations-section p-3 pb-2 mb-2">
                    <div className="section-header mb-2 pb-2">
                      <h5 className="mb-0 d-flex align-items-center">
                        <i className="fa fa-video me-2 text-primary"></i>
                        <strong>Video Animations</strong>
                        <div className="video-info-container">
                          <i className="fa fa-info-circle video-info-icon"></i>
                          <div className="video-info-tooltip">
                            <div className="tooltip-arrow"></div>
                            During a therapy session, you can manually select different head poses and expressions in real-time.
                          </div>
                        </div>
                      </h5>
                      <small className="text-muted">See how your video will look during a therapy session</small>
                    </div>

                    {/* Speech/Dubbing Section */}
                    <div className="row pt-2 animation-preview-wrapper">
                      <div className="col-md-3 col-3 pt-2">
                        <strong>Dubbing</strong>
                      </div>
                      <div className="col-md-3 col-3" key={previews.speech?.label}>
                        <button
                          className={`btn btn-primary upcoming-session-action-button ${selectedPreview?.label === previews.speech?.label ? 'selected' : ''}`}
                          onClick={() => {
                            setVideoLoading(true);
                            setSelectedPreview(previews.speech);
                          }}
                        >
                          Speech
                        </button>
                      </div>
                    </div>

                    {/* Head/Poses Section */}
                    {previews.poses && (
                      <div className="row pt-2 animation-preview-wrapper" key={"poses"}>
                        <div className="col-md-3 col-3 pt-2">
                          <strong>Head Poses</strong>
                        </div>
                        {previews.poses.map((preview) => (
                          <div className="col-md-3 col-3" key={preview.label}>
                            <button
                              className={`btn btn-primary upcoming-session-action-button ${selectedPreview?.label === preview.label ? 'selected' : ''}`}
                              onClick={() => {
                                setVideoLoading(true);
                                setSelectedPreview(preview);
                              }}
                            >
                              {preview.name}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Expressions Section */}
                    {previews.expressions && previews.expressions.length > 0 && (() => {
                      // Sort expressions alphabetically by name
                      const sortedExpressions = [...previews.expressions].sort((a, b) => 
                        a.name.localeCompare(b.name)
                      );
                      const totalPages = Math.ceil(sortedExpressions.length / EXPRESSIONS_PER_PAGE);
                      const startIdx = expressionPage * EXPRESSIONS_PER_PAGE;
                      const currentExpressions = sortedExpressions.slice(startIdx, startIdx + EXPRESSIONS_PER_PAGE);
                      return (
                        <div key={"expressions"}>
                          <div className="row pt-2 animation-preview-wrapper">
                            <div className="col-md-3 col-3 pt-2">
                              <strong style={{display:'block'}}>Expressions</strong>
                              {totalPages > 1 && (
                                <div className="expression-pager d-flex align-items-center justify-content-center" style={{width:'100%'}}>
                                  <i
                                    className={`fa fa-chevron-left expression-pager-arrow ${expressionPage === 0 ? 'disabled' : ''}`}
                                    title="Previous expressions"
                                    onClick={() => expressionPage > 0 && setExpressionPage(p => Math.max(0, p - 1))}
                                  />
                                  <span style={{fontSize:'11px', color:'#6c757d'}}>{expressionPage + 1}/{totalPages}</span>
                                  <i
                                    className={`fa fa-chevron-right expression-pager-arrow ${expressionPage >= totalPages - 1 ? 'disabled' : ''}`}
                                    title="Next expressions"
                                    onClick={() => expressionPage < totalPages - 1 && setExpressionPage(p => Math.min(totalPages - 1, p + 1))}
                                  />
                                </div>
                              )}
                            </div>
                            {currentExpressions.map((preview) => (
                              <div className="col-md-3 col-3" key={preview.label}>
                                <button
                                  className={`btn btn-primary upcoming-session-action-button ${selectedPreview?.label === preview.label ? 'selected' : ''}`}
                                  onClick={() => {
                                    setVideoLoading(true);
                                    setSelectedPreview(preview);
                                  }}
                                >
                                  {preview.name}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Voice Status Message below video animations */}
                  {templateData?.has_voice_file && (
                    <div className="voice-morphing-toggle-section mb-2 p-3" style={{
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                      marginTop: '26px'
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="voice-morphing-compact-toggle d-flex align-items-center">
                          <div className="d-flex align-items-center me-3">
                            <i className="fa fa-microphone me-2" style={{color: '#65badbff'}}></i>
                            <span style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#495057'
                            }}>
                              AI Voice
                            </span>
                          </div>
                          <span className="badge text-white" style={{fontSize: '11px', backgroundColor: '#65badbff'}}>Premium</span>
                        </div>
                        <small className="text-muted" style={{fontSize: '12px', maxWidth: '200px'}}>
                                  {templateData?.enable_morphing
                                    ? "AI voice will be used for speech"
                                    : "Your voice will be used for speech"}
                        </small>
                      </div>
                    </div>
                  )}
                  
                  {/* Voice Selection Section - Only show when voice morphing is enabled (AI voices) */}
                  {templateData?.enable_morphing && (previews.voices && Object.keys(previews.voices).some(gender => Object.keys(previews.voices[gender]).length > 0)) && (
                    <div className="pt-3">
                      <div className={`voice-selection-section p-3 ${hasVoiceSelectionChanged() ? 'voice-changed' : ''} ${!templateData?.voice_self_chosen ? 'default-voice-selection' : ''}`}>
                        <div className="section-header mb-3 pb-2 voice-header">
                          <h5 className="mb-0 d-flex align-items-center">
                            <i className="fa fa-microphone me-2 voice-selection-icon"></i>
                            <strong>Voice Selection</strong>
                            <div className="voice-info-container">
                              <i className="fa fa-info-circle voice-info-icon"></i>
                              <div className="voice-info-tooltip">
                                <div className="tooltip-arrow"></div>
                                Select which AI voice you'd like to use during the therapy session. During the session, you can at all times turn the AI voice off and on.
                              </div>
                            </div>
                            {hasVoiceSelectionChanged() && (
                              <span className="change-indicator ms-2" title="Voice selection has been changed">
                                <i className="fa fa-circle change-dot"></i>
                              </span>
                            )}
                          </h5>
                          <small className="text-muted">
                            {!templateData?.voice_self_chosen 
                              ? 'Default AI voice is selected - Choose a different voice below' 
                              : 'Choose a voice for during the therapy session'}
                          </small>
                        </div>
                        
                        <div className="voice-selector-wrapper">
                          <VoiceSelector 
                            voices={previews.voices}
                            selectedVoice={selectedVoice}
                            onVoiceSelect={handleVoiceSelect}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Additional CSS for animations */}
      <style jsx>{`
        /* Main container */
        .main-container {
          gap: 30px;
        }

        /* Video preview container */
        .video-preview-container {
          background: #f8f9fa;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          border: 1px solid rgba(255,255,255,0.2);
          transition: all 0.3s ease;
        }

        .video-preview-container:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.25) !important;
        }

        /* Video frame */
        .video-frame {
          background: #000;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          width: 100%;
          height: 315px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        /* Video loading skeleton */
        .video-loading-skeleton {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #f8f9fa;
          background-size: 200% 100%;
          animation: loading-shimmer 1.5s infinite;
          z-index: 10;
          border-radius: 12px;
        }

        .loading-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #666;
          font-size: 14px;
        }

        /* Video player */
        .video-player {
          width: 100%;
          height: 100%;
          display: block;
          border-radius: 12px;
          object-fit: contain;
          transition: opacity 0.3s ease;
          position: absolute;
          top: 0;
          left: 0;
        }

        /* Video thumbnail */
        .video-thumbnail {
          width: 100%;
          height: 100%;
          display: block;
          border-radius: 12px;
          object-fit: contain;
          position: absolute;
          top: 0;
          left: 0;
        }

        /* Video overlay */
        .video-overlay {
          position: absolute;
          top: 10px;
          left: 10px;
          background: rgba(0,0,0,0.7);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        /* Selected voice player */
        .selected-voice-player {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.3);
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          transition: all 0.3s ease;
        }

        .selected-voice-player:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0,0,0,0.2) !important;
        }

        .voice-icon {
          color: #65badbff;
        }

        .voice-name {
          color: #2d3436;
          font-size: 16px;
        }

        .voice-description {
          font-size: 13px;
        }

        /* Audio player wrapper */
        .audio-player-wrapper {
          background: rgba(255,255,255,0.9);
          border-radius: 12px;
          padding: 12px;
          box-shadow: inset 0 2px 8px rgba(0,0,0,0.1);
        }

        .audio-player {
          height: 45px;
          border-radius: 8px;
          filter: sepia(20%) saturate(70%) hue-rotate(15deg);
        }

        .audio-player:hover {
          filter: sepia(30%) saturate(80%) hue-rotate(20deg);
        }

        /* Controls column */
        .controls-column {
          flex: 1 1;
        }

        .controls-content {
          min-height: 260px;
        }

        /* Video animations section */
        .video-animations-section {
          background-color: #f8f9fa;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }

        .video-animations-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
        }

        .section-header {
          border-bottom: 2px solid #dee2e6;
        }

        .section-header h5 {
          color: #495057;
          font-weight: 600;
        }

        /* Voice selection section */
        .voice-selection-section {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          transition: all 0.3s ease;
        }

        .voice-selection-section.voice-changed {
          border: 2px solid #ffc107;
          box-shadow: 0 8px 32px rgba(255, 193, 7, 0.3);
        }

        /* Default voice selection animation */
        .voice-selection-section.default-voice-selection {
          background: linear-gradient(135deg, #FFE4B5 0%, #FFD8A8 100%);
          border: 2px solid rgba(255, 140, 0, 0.5);
          animation: voice-section-pulse 2.5s ease-in-out infinite;
          position: relative;
        }

        .voice-selection-section.default-voice-selection:hover {
          animation: none;
          border-color: rgba(255, 140, 0, 0.8);
          box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.3), 0 8px 32px rgba(255, 140, 0, 0.4) !important;
        }

        .voice-selection-section.default-voice-selection .voice-header {
          border-bottom-color: #FF8C00;
        }

        .voice-selection-section.default-voice-selection .voice-selection-icon {
          color: #FF8C00;
        }

        @keyframes voice-section-pulse {
          0%, 100% {
            box-shadow: 0 0 0 2px rgba(255, 140, 0, 0.4), 0 8px 32px rgba(255, 140, 0, 0.2);
            border-color: rgba(255, 140, 0, 0.5);
          }
          50% {
            box-shadow: 0 0 0 4px rgba(255, 140, 0, 0.6), 0 8px 32px rgba(255, 140, 0, 0.4), 0 0 40px rgba(255, 140, 0, 0.3);
            border-color: rgba(255, 140, 0, 0.8);
          }
        }

        /* Default voice badge */
        .default-voice-badge {
          background: linear-gradient(135deg, #FFB347, #FF8C00);
          color: white;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          box-shadow: 0 2px 8px rgba(255, 140, 0, 0.4);
          animation: badge-pulse 1.5s ease-in-out infinite;
        }

        @keyframes badge-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        .voice-selection-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
        }

        .voice-header {
          border-bottom: 2px solid #65badbff;
        }

        .voice-selection-icon {
          color: #65badbff;
        }

        /* Info containers and tooltips */
        .video-info-container,
        .voice-info-container {
          position: relative;
          display: inline-block;
          cursor: pointer;
          margin-left: 8px;
        }

        .video-info-icon,
        .voice-info-icon {
          font-size: 16px;
          color: #17a2b8;
          opacity: 0.7;
          transition: all 0.3s ease;
        }

        .video-info-tooltip,
        .voice-info-tooltip {
          visibility: hidden;
          opacity: 0;
          position: absolute;
          top: 125%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #2c3e50;
          color: #ffffff;
          padding: 16px 24px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.5;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          width: 450px;
          white-space: normal;
          text-align: left;
        }

        .tooltip-arrow {
          content: "";
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-bottom-color: #2c3e50;
        }

        .video-info-container:hover .video-info-tooltip,
        .voice-info-container:hover .voice-info-tooltip {
          visibility: visible !important;
          opacity: 1 !important;
          transform: translateX(-50%) translateY(2px) !important;
        }

        .video-info-container:hover .video-info-icon,
        .voice-info-container:hover .voice-info-icon {
          opacity: 1 !important;
          transform: scale(1.1) !important;
        }

        /* Change indicator */
        .change-dot {
          color: #ffc107;
          font-size: 8px;
        }

        /* Animation controls container */
        .animation-controls-container {
          max-width: 100%;
        }

        /* Voice morphing toggle section */
        .voice-morphing-toggle-section {
          transition: all 0.3s ease;
        }

        .voice-morphing-toggle-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
        }

        .voice-morphing-compact-toggle .form-check-input:checked {
          background-color: #65badbff;
          border-color: #65badbff;
        }

        .voice-morphing-compact-toggle .form-check-input:focus {
          border-color: #3dfff5ff; !important
        }

        /* Loading animation */
        @keyframes loading-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        /* Selected button styling */
        .upcoming-session-action-button.selected {
          background-color: #385cffff !important;
          border-color: #385cffff !important;
          color: white !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(101, 186, 219, 0.3) !important;
        }

        .btn.btn-primary.upcoming-session-action-button.selected {
          background-color: #385cffff !important;
          border-color: #385cffff !important;
          color: white !important;
        }

        .voice-morph-indicator .badge {
          font-size: 12px;
          padding: 6px 10px;
        }

        /* Change indicator animation */
        @keyframes pulse-yellow {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
          }
        }
        
        .change-indicator {
          display: inline-block;
          animation: pulse-yellow 2s infinite;
        }

        /* Expression pager arrows */
        .expression-pager-arrow {
          cursor: pointer;
          color: #6c757d;
          font-size: 12px;
          padding: 4px 6px;
          border-radius: 4px;
          transition: all 0.2s ease;
          background: rgba(255,255,255,0.6);
        }
        .expression-pager-arrow:hover:not(.disabled) {
          color: #495057;
          background: #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .expression-pager-arrow.disabled {
          opacity: 0.35;
          cursor: default;
          pointer-events: none;
        }
      `}</style>
    </>
  );
});

export default AnimationPreviews;
